import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X, Star, Copy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatDate } from '../lib/format';

const PLATFORMS = ['instagram','youtube','threads','facebook','whatsapp','website'];

export default function Portfolio() {
  const [assets, setAssets] = useState([]);
  const [sets, setSets] = useState([]);
  const [tab, setTab] = useState('assets');
  const [edit, setEdit] = useState(null);
  const [editSet, setEditSet] = useState(null);
  const load = () => Promise.all([
    api.get('/portfolio-assets').then(r => setAssets(r.data.filter(a => !a.deleted_at))),
    api.get('/portfolio-sets').then(r => setSets(r.data.filter(s => !s.deleted_at))),
  ]);
  useEffect(() => { load(); }, []);

  const save = async (a) => {
    if (a.id) await api.put(`/portfolio-assets/${a.id}`, a);
    else await api.post('/portfolio-assets', a);
    toast.success('Saved'); setEdit(null); load();
  };
  const saveSet = async (s) => {
    if (s.id) await api.put(`/portfolio-sets/${s.id}`, s);
    else await api.post('/portfolio-sets', s);
    toast.success('Saved'); setEditSet(null); load();
  };

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div><div className="eyebrow">Media</div><h1 className="serif text-[28px]">Portfolio</h1></div>
        <div className="flex gap-2">
          {tab==='assets' && <button className="btn-gold" onClick={() => setEdit({ url: '', consent: {} })} data-testid="asset-add"><Plus size={14}/> Add Image</button>}
          {tab==='sets' && <button className="btn-gold" onClick={() => setEditSet({ name: '', asset_ids: [], audience: 'client' })} data-testid="set-add"><Plus size={14}/> New Set</button>}
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab('assets')} className="px-3 py-1.5 rounded-full text-xs tracking-[0.14em] uppercase" style={{background: tab==='assets'?'var(--charcoal)':'transparent', color: tab==='assets'?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}>Assets ({assets.length})</button>
        <button onClick={() => setTab('sets')} className="px-3 py-1.5 rounded-full text-xs tracking-[0.14em] uppercase" style={{background: tab==='sets'?'var(--charcoal)':'transparent', color: tab==='sets'?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}>Curated Sets ({sets.length})</button>
      </div>

      {tab === 'assets' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {assets.length === 0 && <div className="col-span-full card-cream p-8 text-center text-sm text-[var(--muted)] italic">No images yet. Add image URLs (uploads will be in a future phase).</div>}
          {assets.map(a => (
            <div key={a.id} className="card-cream p-2 cursor-pointer hover:shadow" onClick={() => setEdit(a)} data-testid={`asset-${a.id}`}>
              <div className="aspect-square overflow-hidden bg-[var(--cream-alt)] rounded-sm">
                {a.url ? <img src={a.url} alt={a.caption} className="w-full h-full object-cover" style={{objectPosition:'center'}}/> : <div className="w-full h-full flex items-center justify-center text-xs text-[var(--muted)]">No image</div>}
              </div>
              <div className="mt-2 text-xs">
                <div className="truncate">{a.caption || 'Untitled'}</div>
                <div className="flex gap-1 flex-wrap mt-1">
                  {PLATFORMS.filter(p => a.consent?.[p]).map(p => <span key={p} className="text-[9px] px-1 py-0.5 rounded" style={{background:'rgba(107,143,113,0.14)', color:'var(--ok)'}}>{p}</span>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'sets' && (
        <div className="space-y-2">
          {sets.length === 0 && <div className="card-cream p-8 text-center text-sm text-[var(--muted)] italic">No portfolio sets yet.</div>}
          {sets.map(s => (
            <div key={s.id} className="card-cream p-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="serif text-[18px]">{s.name} <span className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase ml-2">{s.audience}</span></div>
                <div className="text-xs text-[var(--muted)]">{s.asset_ids?.length || 0} images · {s.view_count || 0} views · {s.expires_on?`expires ${formatDate(s.expires_on)}`:'no expiry'}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/public/portfolio-set/${s.id}`); toast.success('Link copied'); }} className="btn-ghost text-xs"><Copy size={12}/> Copy link</button>
                <button onClick={() => setEditSet(s)} className="btn-ghost text-xs">Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && <AssetForm value={edit} onClose={() => setEdit(null)} onSave={save} />}
      {editSet && <SetForm value={editSet} assets={assets} onClose={() => setEditSet(null)} onSave={saveSet}/>}
    </div>
  );
}

function AssetForm({ value, onClose, onSave }) {
  const [f, setF] = useState({ consent: {}, look_tags: [], ...value });
  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
      <div className="w-full max-w-lg card-cream p-6 lg:rounded-md overflow-y-auto max-h-[92vh]">
        <div className="flex items-center justify-between mb-4"><h2 className="serif text-[22px]">{value?.id?'Edit Image':'Add Image'}</h2><button onClick={onClose}><X size={18}/></button></div>
        <div className="space-y-3">
          <div><label className="eyebrow">Image URL</label><input value={f.url||''} onChange={e => setF({ ...f, url: e.target.value })} placeholder="https://…" data-testid="asset-form-url"/></div>
          <div><label className="eyebrow">Caption</label><input value={f.caption||''} onChange={e => setF({ ...f, caption: e.target.value })}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Event type</label><input value={f.event_type||''} onChange={e => setF({ ...f, event_type: e.target.value })}/></div>
            <div><label className="eyebrow">City</label><input value={f.city||''} onChange={e => setF({ ...f, city: e.target.value })}/></div>
            <div><label className="eyebrow">Photographer</label><input value={f.photographer||''} onChange={e => setF({ ...f, photographer: e.target.value })}/></div>
            <div><label className="eyebrow">Date</label><input type="date" value={f.date||''} onChange={e => setF({ ...f, date: e.target.value })}/></div>
          </div>
          <div><label className="eyebrow">Look tags (comma sep)</label><input value={(f.look_tags||[]).join(',')} onChange={e => setF({ ...f, look_tags: e.target.value.split(',').map(x=>x.trim()).filter(Boolean) })}/></div>
          <div>
            <label className="eyebrow">Consent — publishable to</label>
            <div className="grid grid-cols-2 gap-1 mt-1">
              {PLATFORMS.map(p => (
                <label key={p} className="flex items-center gap-2 text-xs py-1">
                  <input type="checkbox" style={{width:'auto'}} checked={!!f.consent?.[p]} onChange={e => setF({ ...f, consent: { ...f.consent, [p]: e.target.checked } })} data-testid={`asset-consent-${p}`}/>
                  {p}
                </label>
              ))}
            </div>
            <div className="text-[10px] text-[var(--muted)] mt-1">Only tick if a signed consent form exists for this project + platform.</div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" style={{width:'auto'}} checked={!!f.favourite} onChange={e => setF({ ...f, favourite: e.target.checked })}/> <Star size={12}/> Favourite</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" style={{width:'auto'}} checked={!!f.published} onChange={e => setF({ ...f, published: e.target.checked })}/> Published</label>
          <button className="btn-gold w-full" onClick={() => onSave(f)} data-testid="asset-form-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function SetForm({ value, assets, onClose, onSave }) {
  const [f, setF] = useState({ asset_ids: [], audience: 'client', ...value });
  const toggle = (id) => setF({ ...f, asset_ids: f.asset_ids.includes(id) ? f.asset_ids.filter(x => x !== id) : [...f.asset_ids, id] });
  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
      <div className="w-full max-w-2xl card-cream p-6 lg:rounded-md overflow-y-auto max-h-[92vh]">
        <div className="flex items-center justify-between mb-4"><h2 className="serif text-[22px]">{value?.id?'Edit Set':'New Set'}</h2><button onClick={onClose}><X size={18}/></button></div>
        <div className="space-y-3">
          <div><label className="eyebrow">Internal name</label><input value={f.name||''} onChange={e => setF({ ...f, name: e.target.value })} data-testid="set-form-name"/></div>
          <div><label className="eyebrow">Title (shown to recipient)</label><input value={f.title||''} onChange={e => setF({ ...f, title: e.target.value })}/></div>
          <div><label className="eyebrow">Intro</label><textarea rows={3} value={f.intro||''} onChange={e => setF({ ...f, intro: e.target.value })}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Audience</label>
              <select value={f.audience} onChange={e => setF({ ...f, audience: e.target.value })}>
                <option value="client">Client-facing</option><option value="b2b">B2B / professional</option>
              </select>
            </div>
            <div><label className="eyebrow">Expires on</label><input type="date" value={f.expires_on||''} onChange={e => setF({ ...f, expires_on: e.target.value })}/></div>
          </div>
          {f.audience === 'b2b' && <label className="flex items-center gap-2 text-sm"><input type="checkbox" style={{width:'auto'}} checked={!!f.include_credentials} onChange={e => setF({ ...f, include_credentials: e.target.checked })}/> Include professional credentials (B2B only)</label>}
          <div>
            <label className="eyebrow">Select assets ({f.asset_ids.length})</label>
            <div className="grid grid-cols-4 gap-2 mt-2 max-h-64 overflow-y-auto">
              {assets.map(a => (
                <div key={a.id} onClick={() => toggle(a.id)} className="cursor-pointer aspect-square overflow-hidden rounded-sm" style={{border: f.asset_ids.includes(a.id)?'2px solid var(--gold)':'1px solid var(--line)'}}>
                  {a.url && <img src={a.url} className="w-full h-full object-cover"/>}
                </div>
              ))}
            </div>
          </div>
          <button className="btn-gold w-full" onClick={() => onSave(f)} data-testid="set-form-save">Save</button>
        </div>
      </div>
    </div>
  );
}
